import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, ChevronLeft, ChevronRight, TrendingUp, Filter, X, Calendar } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => n == null || isNaN(n) ? '0,00' : Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PER_PAGE = 15;

export default function PositionsModal({ open, onClose }) {
  const { user, token } = useAuth();
  const [positions, setPositions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [tradeType, setTradeType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchPositions = useCallback(async (p = 1) => {
    if (!user?.id || !token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: PER_PAGE });
      if (search) params.append('q', search);
      if (category && category !== 'all') params.append('category', category);
      if (tradeType && tradeType !== 'all') params.append('trade_type', tradeType);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const r = await axios.get(`${API}/trade-positions/${user.id}/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPositions(r.data.positions || []);
      setTotal(r.data.total || 0);
      setTotalProfit(r.data.total_profit || 0);
      setTotalPages(r.data.total_pages || 1);
      setPage(p);
    } catch (e) {
      console.error('Positions fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.id, token, search, category, tradeType, dateFrom, dateTo]);

  useEffect(() => {
    if (open) {
      fetchPositions(1);
    }
  }, [open, fetchPositions]);

  const handleSearch = () => fetchPositions(1);
  const clearFilters = () => {
    setSearch('');
    setCategory('all');
    setTradeType('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = search || (category && category !== 'all') || (tradeType && tradeType !== 'all') || dateFrom || dateTo;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col" data-testid="positions-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Toutes les positions
          </DialogTitle>
          <DialogDescription>
            {total} position{total > 1 ? 's' : ''} · P&L total: <span className={totalProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}>{totalProfit >= 0 ? '+' : ''}{fmt(totalProfit)} EUR</span>
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="space-y-3 border-b pb-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Rechercher un actif..."
                className="pl-9 h-9 text-sm"
                data-testid="positions-search"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5 h-9" data-testid="positions-toggle-filters">
              <Filter className="w-3.5 h-3.5" />
              Filtres
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={() => { clearFilters(); setTimeout(() => fetchPositions(1), 0); }} className="gap-1 h-9 text-muted-foreground">
                <X className="w-3.5 h-3.5" />
                Réinitialiser
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Select value={category} onValueChange={v => { setCategory(v); }}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Catégorie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  <SelectItem value="forex">Forex</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                  <SelectItem value="bourse">Bourse</SelectItem>
                  <SelectItem value="matiere_premiere">Matières premières</SelectItem>
                  <SelectItem value="indices">Indices</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tradeType} onValueChange={v => { setTradeType(v); }}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types</SelectItem>
                  <SelectItem value="BUY">Achat</SelectItem>
                  <SelectItem value="SELL">Vente</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 text-xs pl-8" placeholder="Du" data-testid="positions-date-from" />
              </div>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 text-xs pl-8" placeholder="Au" data-testid="positions-date-to" />
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : positions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <TrendingUp className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">Aucune position trouvée</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background z-10">
                <tr className="text-muted-foreground text-xs border-b">
                  <th className="text-left py-2.5 px-2 font-semibold">Actif</th>
                  <th className="text-left py-2.5 px-2 font-semibold">Catégorie</th>
                  <th className="text-left py-2.5 px-2 font-semibold">Type</th>
                  <th className="text-right py-2.5 px-2 font-semibold">Montant</th>
                  <th className="text-right py-2.5 px-2 font-semibold">P&L</th>
                  <th className="text-right py-2.5 px-2 font-semibold">Date</th>
                  <th className="text-right py-2.5 px-2 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => {
                  const pl = p.profit || 0;
                  const sym = p.symbol || p.asset || '-';
                  const cat = p.category || '-';
                  const dt = p.trade_date || (p.close_at ? new Date(p.close_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : p.scheduled_date ? new Date(p.scheduled_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-');
                  const statusLabel = p.status === 'closed' ? 'Fermé' : p.status === 'visible' ? 'Visible' : p.status === 'open' ? 'Ouvert' : p.status;
                  const statusColor = p.status === 'closed' ? 'text-muted-foreground bg-muted/50' : 'text-emerald-500 bg-emerald-500/10';
                  return (
                    <tr key={p.id || i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-2.5 px-2 font-medium">{sym}</td>
                      <td className="py-2.5 px-2 text-muted-foreground text-xs capitalize">{cat}</td>
                      <td className="py-2.5 px-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${p.trade_type === 'BUY' || p.direction === 'BUY' ? 'text-emerald-500 bg-emerald-500/10' : 'text-red-400 bg-red-400/10'}`}>
                          {p.trade_type || p.direction || '-'}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-muted-foreground">{fmt(p.investment_amount || p.amount || 0)}</td>
                      <td className={`py-2.5 px-2 text-right font-semibold ${pl >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                        {pl >= 0 ? '+' : ''}{fmt(pl)}
                      </td>
                      <td className="py-2.5 px-2 text-right text-muted-foreground text-xs">{dt}</td>
                      <td className="py-2.5 px-2 text-right">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-3 border-t">
            <p className="text-xs text-muted-foreground">
              Page {page} sur {totalPages} · {total} résultat{total > 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchPositions(page - 1)} className="h-8 w-8 p-0" data-testid="positions-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm" onClick={() => fetchPositions(p)} className="h-8 w-8 p-0 text-xs">
                    {p}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => fetchPositions(page + 1)} className="h-8 w-8 p-0" data-testid="positions-next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
